import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from 'react-select';
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonPurchaseOrderFields from "../CommonPurchaseOrderFields";
import ProductTable from "../../../Common/ProductTable";
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";

// Helper function
const createEmptyCharge = () => ({
  id: Date.now(),
  description: "",
  chargepercent: 0,
  amount: 0,
  gstPercent: 0,
  isPercentBased: true,
});

const POAMCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Purchase Order Fields State
  const [poData, setPoData] = useState({
    enquiryno: '',
    enquirydate: '',
    shipto: '',
    freight: '',
    pandf: '',
    paymentterms: '',
    deliveryterm: '',
    supplier: null,
    supplierDetails: {
      contactperson: '',
      contactno: '',
      email: ''
    },
    supplierMultipleAddress: null,
    branch: null,
    warehouse: null,
    remarks: "",
    deliverytime: "",
    certificate: "",
    quotationreferencedate: '',
    quotationreferencenumber: ''
  });

  // Product and charges state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([createEmptyCharge()]);
  const [loadingPrices, setLoadingPrices] = useState({});
  const [podate, setpodate] = useState('');
  const [jobnumber, setjobnumber] = useState("");
 
  // API Functions
  const getPumpProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );
      const products = response.data.Index || response.data.data || [];
      setProductOptions(
        products.map((product) => ({
          value: product.id,
          label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
          description: product.description,
          uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
          igst: product.igst || 0,
          partno: product.partno,
          serialnumber: product.serialnumber,
          modelnumber: product.modelnumber,
          tagnumber: product.tagnumber,
          hsnsacno: product.hsnsacno,
          moc: product.moc || "",
          gradeofmaterial: product.gradeofmaterial || "",
          type: product.type || "",
          size: product.size || "",
          rating: product.rating || "",
          classes: product.classes || "",
          schedule: product.schedule || "",
          certification: product.certification || "",
          unitrateinr: product.unitrateinr || 0,
        }))
      );
    } catch (error) {
      Swal.fire("Error", "Failed to fetch product data", "error");
    }
  };

  const fetchLastPurchasePrice = async (supplierId, productId) => {
    try {
      if (!supplierId || !productId) return null;

      setLoadingPrices(prev => ({ ...prev, [productId]: true }));

      const response = await axios.get(
        `${BASE_URL}purchase/historyPrice/supplierandproduct/${supplierId}/${productId}`,
        { headers: authHeader() }
      );

      const price = response.data.price || response.data.unitPrice || response.data;
      return typeof price === 'number' ? price : null;
    } catch (error) {
      console.error("Error fetching last purchase price:", error);
      return null;
    } finally {
      setLoadingPrices(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
  }, 500);

  // Product data effect
  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  // Update product prices when supplier changes
  useEffect(() => {
    const updateProductPrices = async () => {
      const supplierId = poData.supplier?.value;
      if (!supplierId) return;

      const updatedRows = [...rows];

      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i];
        const price = await fetchLastPurchasePrice(supplierId, row.value);

        if (price !== null) {
          updatedRows[i].unitRateInr = price;
          updatedRows[i].lastPurchasePrice = price;

          // Recalculate amount and total
          const quantity = parseFloat(updatedRows[i].quantity) || 0;
          const gstPercent = parseFloat(updatedRows[i].gstPercent) || 0;

          const amount = quantity * price;
          const gstAmount = (amount * gstPercent) / 100;
          const totalinr = amount + gstAmount;

          updatedRows[i].amount = amount;
          updatedRows[i].totalinr = totalinr;
        }
      }

      setRows(updatedRows);
    };

    updateProductPrices();
  }, [poData.supplier]);

  // Product handlers
  const handleProductChange = async (selectedOption) => {
    setSelectedProduct(selectedOption);
    const selectedProductData = productOptions.find(
      (product) => product.value === selectedOption.value
    );

    if (selectedProductData) {
      const supplierId = poData.supplier?.value;
      let lastPurchasePrice = 0;

      if (supplierId) {
        lastPurchasePrice = await fetchLastPurchasePrice(supplierId, selectedProductData.value) || 0;
      }

      const newRow = createProductRow(selectedProductData, lastPurchasePrice);
      setRows([...rows, newRow]);
    }
  };

  const createProductRow = (productData, lastPurchasePrice) => ({
    description: productData.description,
    value: productData.value,
    label: productData.label,
    productremarks: "",
    quantity: "",
    uom: productData.uom || "",
    unitRateInr: lastPurchasePrice || productData.unitrateinr || 0,
    gstPercent: productData.igst || 0,
    amount: (lastPurchasePrice || productData.unitrateinr || 0),
    totalinr: (lastPurchasePrice || productData.unitrateinr || 0) * (1 + (productData.igst || 0) / 100),
    hsnsacno: productData.hsnsacno,
    partno: productData.partno,
    serialnumber: productData.serialnumber,
    moc: productData.moc || "",
    gradeofmaterial: productData.gradeofmaterial || "",

    type: productData.type || "",
    size: productData.size || "",
    rating: productData.rating || "",
    classes: productData.classes || "",
    schedule: productData.schedule || "",
    certification: productData.certification || "",
    lastPurchasePrice: lastPurchasePrice,
  });

  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
      const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

      const amount = quantity * unitRateInr;
      const gstAmount = (amount * gstPercent) / 100;
      const totalinr = amount + gstAmount;

      updatedRows[index].amount = amount;
      updatedRows[index].totalinr = totalinr;
    }

    setRows(updatedRows);
  };

  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Charges handlers - Fixed for 2 decimal places
  const handleChargeChange = (index, field, value, isBlur = false) => {
    setAdditionalCharges(prev => {
      const newCharges = [...prev];
      const charge = { ...newCharges[index] };
      const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

      if (field === "chargepercent") {
        const percentage = Math.min(value, 100);
        charge.chargepercent = parseFloat(percentage.toFixed(2));

        if (isBlur || percentage === 100) {
          const calculatedAmount = (percentage * netTotal) / 100;
          charge.amount = parseFloat(calculatedAmount.toFixed(2));
        } else {
          charge.amount = 0;
        }
      } else if (field === "amount") {
        charge.amount = parseFloat(value.toFixed(2));
        charge.chargepercent = 0;
      } else if (field === "gstPercent") {
        charge.gstPercent = parseFloat(value.toFixed(2));
      } else {
        charge[field] = value;
      }

      newCharges[index] = charge;
      return newCharges;
    });
  };

  const addNewChargeRow = () => {
    setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
  };

  const removeChargeRow = (index) => {
    if (additionalCharges.length > 1) {
      setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Calculations - Fixed for 2 decimal places
  const { productsTotal, additionalChargesTotal, grossTotal, grandTotal } = useMemo(() => {
    const productsTotal = rows.reduce((total, row) => {
      const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
      return total + rowTotal;
    }, 0);

    const additionalChargesTotal = additionalCharges.reduce((total, charge) => {
      const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
      return total + chargeTotal;
    }, 0);

    const grossTotal = productsTotal + additionalChargesTotal;
    const roundedGrossTotal = Math.round(grossTotal);
    const grandTotal = roundedGrossTotal;

    return {
      productsTotal: parseFloat(productsTotal.toFixed(2)),
      additionalChargesTotal: parseFloat(additionalChargesTotal.toFixed(2)),
      grossTotal: parseFloat(grossTotal.toFixed(2)),
      grandTotal
    };
  }, [rows, additionalCharges]);

  // Form validation
  const validateForm = () => {
    if (!poData.supplier) {
      Swal.fire("Missing Supplier", "Please select a supplier before submitting the form.", "error");
      return false;
    }

    if (!poData.supplierMultipleAddress) {
      Swal.fire("Missing Address", "Please select the supplier's address.", "error");
      return false;
    }

    if (!poData.branch) {
      Swal.fire("Missing Branch", "Please select a branch.", "error");
      return false;
    }

    if (!poData.warehouse) {
      Swal.fire("Missing Warehouse", "Please select a warehouse.", "error");
      return false;
    }

    if (rows.length === 0) {
      Swal.fire("No Products Added", "Please add at least one product to continue.", "error");
      return false;
    }

    for (const row of rows) {
      if (!row.quantity || isNaN(row.quantity) || row.quantity <= 0) {
        Swal.fire(
          "Invalid Quantity",
          "Each product must have a valid quantity greater than 0.",
          "error"
        );
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    const purchaseOrderItems = rows.map(row => ({
      product: { id: row.value },
      quantity: row.quantity,
      uom: row.uom,
      unitrateinr: row.unitRateInr,
      gst: row.gstPercent,
      amount: row.amount,
      totalinr: row.totalinr,
      productremarks: row.productremarks,
      partno: row.partno || "",
      hsnsacno: row.hsnsacno || "",
      serialnumber: row.serialnumber || "",
      moc: row.moc || "",
      gradeofmaterial: row.gradeofmaterial || "",
      type: row.type || "",
      size: row.size || "",
      rating: row.rating || "",
      classes: row.classes || "",
      schedule: row.schedule || "",
      certification: row.certification || "",
    }));

    // Prepare additional charges - Fixed for 2 decimal places
    const additionalChargesData = additionalCharges
      .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
      .map((charge) => {
        const chargeAmount = parseFloat(charge.amount.toFixed(2));
        const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
        const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
        const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

        return {
          chargetype: charge.description,
          chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
          chargeamount: chargeAmount,
          chargegst: gstPercent,
          chargetotal: chargetotal,
          isPercentBased: charge.isPercentBased,
        };
      });

    // Calculate totals - Fixed for 2 decimal places
    const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
      (sum, charge) => sum + charge.chargetotal,
      0
    ).toFixed(2));

    const productsTotalCalc = parseFloat(purchaseOrderItems.reduce(
      (sum, item) => sum + item.totalinr,
      0
    ).toFixed(2));

    const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

    const purchaseData = {
      purchaseOrderItems,
      additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
      nettotalproduct: productsTotalCalc,
      additionalchargestotal: additionalChargesTotalCalc,
      grosstotal: grossTotalCalc,
      potype: 'Manufacturing',
      pocategory: 'Domestic',
      podate,
      jobnumber,
      // pono remove kiya - backend auto generate karega
      supplier: { id: poData.supplier.value },
      contactno: poData.supplierDetails.contactno,
      contactperson: poData.supplierDetails.contactperson,
      email: poData.supplierDetails.email,
      supplierMultipleAddress: { id: poData.supplierMultipleAddress.value },
      branch: poData.branch ? { id: poData.branch.value } : null,
      warehouse: poData.warehouse ? { id: poData.warehouse.value } : null,
      shipto: poData.shipto,
      freight: poData.freight,
      pandf: poData.pandf,
      paymentterms: poData.paymentterms,
      deliveryterm: poData.deliveryterm,
      enquiryno: poData.enquiryno,
      enquirydate: poData.enquirydate,
      deliverytime: poData.deliverytime,
      remarks: poData.remarks,
      certificate: poData.certificate,
      quotationreferencedate: poData.quotationreferencedate,
      quotationreferencenumber: poData.quotationreferencenumber,

    };

    try {
      const response = await axios.post(`${BASE_URL}purchaseOrder/save`, purchaseData, {
        headers: authHeader()
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Purchase Order created successfully!',
      });
      navigate("/poindex/1");
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data || 'Failed to create purchase order',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <br />
      <Form>
        {/* Purchase Order Date Only - PO Number remove kiya */}
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Purchase Order Date</Form.Label>
              <Form.Control
                type="date"
                value={podate}
                onChange={e => setpodate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Job Number</Form.Label>
              <Form.Control
                type="text"
                value={jobnumber}
                onChange={(e) => setjobnumber(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonPurchaseOrderFields
          initialData={poData}
          onDataChange={setPoData}
        />

        <hr />
        <br />

        {/* Products Section */}
        <div className="mt-4">
          <h5>Products</h5>
          <Row>
            <Col md="12">
              <Form.Group>
                <Form.Label>Select Product</Form.Label>
                <Select
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={productOptions}
                  placeholder="Search and select product"
                  onInputChange={handleProductSearchChange}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <ProductTable
            rows={rows}
            unitOptions={UNIT_OPTIONS}
            onRowChange={handleRowChange}
            onRemoveRow={removeRow}
            loadingPrices={loadingPrices}
          />
        </div>

        {/* Charges Section */}
        <ChargesSection
          additionalCharges={additionalCharges}
          chargeOptions={CHARGE_OPTIONS}
          productsTotal={productsTotal}
          additionalChargesTotal={additionalChargesTotal}
          grossTotal={grossTotal}
          grandTotal={grandTotal}
          onChargeChange={handleChargeChange}
          onAddCharge={addNewChargeRow}
          onRemoveCharge={removeChargeRow}
        />

        {/* Submit Button */}
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" />
              {' Submitting...'}
            </>
          ) : 'SUBMIT'}
        </button>
      </Form>
    </div>
  );
};

export default POAMCreate;